import React, { useState, useEffect } from 'react';
import { MedicationAutocomplete } from './MedicationAutocomplete';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, MapPin, Pill, Save, Check, AlertCircle } from 'lucide-react';
import { HeadacheFormData } from '../../types';
import { format } from 'date-fns';

interface HeadacheFormProps {
  onSubmit: (data: HeadacheFormData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<HeadacheFormData>;
  initialDate?: Date;
}

const intensityOptions = [
  { value: 'leve', label: 'Leve', emoji: '😊', color: '#22c55e' },
  { value: 'moderada', label: 'Moderada', emoji: '😐', color: '#f59e0b' },
  { value: 'fuerte', label: 'Fuerte', emoji: '😣', color: '#ef4444' },
];

const locationOptions = [
  { value: 'Frente', icon: '👁️' },
  { value: 'Sien', icon: '⚡' },
  { value: 'Parte Superior', icon: '⬆️' },
  { value: 'Nuca', icon: '🔙' },
  { value: 'Toda la Cabeza', icon: '🤕' },
];

export const HeadacheForm: React.FC<HeadacheFormProps> = ({
  onSubmit,
  onCancel,
  initialData = {},
  initialDate,
}) => {
  // Format a Date object to datetime-local input format
  const formatDateForInput = (date: Date, useCurrentTime: boolean = false): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const now = new Date();
    const hours = String(useCurrentTime ? now.getHours() : date.getHours()).padStart(2, '0');
    const minutes = String(useCurrentTime ? now.getMinutes() : date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Parse ISO date string from API to datetime-local format
  const parseApiDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return formatDateForInput(new Date(), true);
      return formatDateForInput(date, false);
    } catch {
      return formatDateForInput(new Date(), true);
    }
  };

  // Format time from API (could be "HH:mm:ss" or "HH:mm")
  const parseApiTime = (timeStr: string | undefined): string => {
    if (!timeStr) return '';
    // Extract HH:mm from possible HH:mm:ss format
    return timeStr.substring(0, 5);
  };

  // Determine initial datetime value
  const getInitialDatetime = (): string => {
    if (initialData.date) {
      return parseApiDate(initialData.date);
    }
    return formatDateForInput(initialDate || new Date(), true);
  };

  const [formData, setFormData] = useState({
    datetime: getInitialDatetime(),
    intensity: initialData.intensity || '',
    location: initialData.location || '',
    medication_taken: initialData.medication_taken ?? null,
    medication: initialData.medication || '',
    medication_time: parseApiTime(initialData.medication_time),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only update if adding new (not editing) and initialDate changes
    if (!initialData.date && initialDate) {
      setFormData(prev => ({
        ...prev,
        datetime: formatDateForInput(initialDate, true),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate]);

  const handleLocationToggle = (location: string) => {
    const currentLocations = formData.location ? formData.location.split(', ').filter(Boolean) : [];

    if (location === 'Toda la Cabeza') {
      if (currentLocations.includes('Toda la Cabeza')) {
        setFormData(prev => ({ ...prev, location: '' }));
      } else {
        setFormData(prev => ({ ...prev, location: locationOptions.map(l => l.value).join(', ') }));
      }
    } else {
      if (currentLocations.includes('Toda la Cabeza')) {
        return;
      }

      if (currentLocations.includes(location)) {
        setFormData(prev => ({
          ...prev,
          location: currentLocations.filter(l => l !== location).join(', '),
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          location: [...currentLocations, location].join(', '),
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.intensity) {
      setError('Por favor, selecciona la intensidad del dolor');
      return;
    }

    if (formData.medication_taken === null) {
      setError('Por favor, indica si tomaste medicación');
      return;
    }

    if (formData.medication_taken) {
      if (!formData.medication.trim()) {
        setError('Por favor, indica qué medicamento tomaste');
        return;
      }
      if (!formData.medication_time) {
        setError('Por favor, indica a qué hora tomaste la medicación');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const submitData: HeadacheFormData = {
        intensity: formData.intensity as 'leve' | 'moderada' | 'fuerte',
        location: formData.location || undefined,
        medication_taken: formData.medication_taken,
        medication: formData.medication_taken ? formData.medication : undefined,
        medication_time: formData.medication_taken ? formData.medication_time : undefined,
        date: formData.datetime + ':00',
      };

      await onSubmit(submitData);
    } catch (err: any) {
      setError(err.message || 'Error al guardar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedLocations = formData.location ? formData.location.split(', ').filter(Boolean) : [];
  const isTodaLaCabezaSelected = selectedLocations.includes('Toda la Cabeza');

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-2 p-3.5 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 text-red-600 dark:text-red-400 text-sm"
        >
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </motion.div>
      )}

      {/* Fecha y hora */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)] mb-2">
          <Clock size={15} className="text-[var(--color-accent)]" />
          Fecha y hora
        </label>
        <input
          type="datetime-local"
          value={formData.datetime}
          onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
          className="input"
          required
        />
      </div>

      {/* Intensidad — barras tipo indicador de nivel, sin emojis */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-text)] mb-3">
          Intensidad del dolor
        </label>
        <div className="grid grid-cols-3 gap-2.5">
          {intensityOptions.map((option, index) => {
            const level = index + 1;
            const selected = formData.intensity === option.value;
            return (
              <motion.button
                key={option.value}
                type="button"
                whileTap={{ scale: 0.96 }}
                onClick={() => setFormData({ ...formData, intensity: option.value })}
                className="flex flex-col items-center gap-2.5 py-4 rounded-2xl border-2 transition-all"
                style={{
                  borderColor: selected ? option.color : 'var(--color-border)',
                  background: selected ? `${option.color}14` : 'var(--color-surface)',
                }}
              >
                <span className="flex items-end gap-1 h-6" aria-hidden>
                  {[0, 1, 2].map((b) => (
                    <span
                      key={b}
                      className="w-1.5 rounded-full transition-all"
                      style={{
                        height: `${(b + 1) * 7 + 3}px`,
                        background: b < level ? option.color : 'var(--color-border)',
                        opacity: b < level ? (selected ? 1 : 0.5) : 1,
                      }}
                    />
                  ))}
                </span>
                <span
                  className="text-sm font-semibold"
                  style={{ color: selected ? option.color : 'var(--color-text)' }}
                >
                  {option.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Ubicación del dolor */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)] mb-3">
          <MapPin size={15} className="text-[var(--color-accent)]" />
          Ubicación del dolor
          <span className="text-xs font-normal text-[var(--color-text-muted)]">· opcional</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {locationOptions.map((option) => {
            const isSelected = selectedLocations.includes(option.value);
            const isGrayedOut = isTodaLaCabezaSelected && option.value !== 'Toda la Cabeza';

            return (
              <motion.button
                key={option.value}
                type="button"
                whileTap={{ scale: 0.96 }}
                onClick={() => handleLocationToggle(option.value)}
                className={`px-3.5 py-2 rounded-full border-2 transition-all flex items-center gap-1.5 text-sm font-medium ${
                  isSelected
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
                } ${isGrayedOut ? 'opacity-40' : ''}`}
              >
                {isSelected && <Check size={14} strokeWidth={3} />}
                {option.value}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Medicación */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)] mb-3">
          <Pill size={15} className="text-[var(--color-accent)]" />
          ¿Tomaste medicación?
        </label>
        <div className="grid grid-cols-2 gap-2.5">
          <motion.button
            type="button"
            whileTap={{ scale: 0.96 }}
            onClick={() => setFormData({ ...formData, medication_taken: false })}
            className={`flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 font-semibold transition-all ${
              formData.medication_taken === false
                ? 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-500'
                : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
            }`}
          >
            <X size={18} strokeWidth={2.5} />
            No
          </motion.button>
          <motion.button
            type="button"
            whileTap={{ scale: 0.96 }}
            onClick={() => setFormData({ ...formData, medication_taken: true })}
            className={`flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 font-semibold transition-all ${
              formData.medication_taken === true
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-600'
                : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
            }`}
          >
            <Check size={18} strokeWidth={2.5} />
            Sí
          </motion.button>
        </div>
      </div>

      {/* Detalles de medicación */}
      <AnimatePresence>
        {formData.medication_taken && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4 overflow-hidden"
          >
            <div>
              <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
                ¿Qué medicamento tomaste?
              </label>
              <MedicationAutocomplete
                value={formData.medication}
                onChange={(medication) => setFormData({ ...formData, medication })}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
                ¿A qué hora lo tomaste?
              </label>
              <input
                type="time"
                value={formData.medication_time}
                onChange={(e) => setFormData({ ...formData, medication_time: e.target.value })}
                className="input"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Botones */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 py-3.5 rounded-xl border-2 border-[var(--color-border)] text-[var(--color-text)] font-medium hover:bg-[var(--color-surface)] transition-colors"
        >
          Cancelar
        </button>
        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileTap={{ scale: 0.98 }}
          className="flex-1 btn btn-primary py-3.5"
        >
          {isSubmitting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Save size={20} />
              Guardar
            </>
          )}
        </motion.button>
      </div>
    </form>
  );
};
